import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaEdit, FaKey, FaFileAlt, FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { uploadFile } from '../../upload-file';
import { Page, PageHeader, Card, SearchInput, Badge, Switch, Modal, Field, EmptyRow, Alert, Loading, initials } from '../../ui';
import { verificationTone, docLabel } from '../../ui/staff';
import { api } from '../../ui/api';
import { getAccount, documentLink } from '../../session';

const DOCS = ['aadhar', 'policeVerification', 'bankDetails', 'panCard'];
const EMPTY = { name: '', email: '', phone: '', password: '', aadhar: '' };

const ManageStaff = () => {
  const vendor = getAccount();
  const [staff, setStaff] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState(null); // 'add' | 'edit' | 'password' | 'view'
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({});
  const [photo, setPhoto] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api(`/staff/get_staff_by_vendor/${vendor.id}`).then(setStaff).catch((e) => { setLoadError(e.message); setStaff([]); });
  }, [vendor.id]);
  useEffect(() => { load(); }, [load]);

  const open = (nextMode, member = null) => {
    setCurrent(member);
    setError('');
    setProgress(0);
    setFiles({});
    setPhoto(null);
    setForm(member
      ? { name: member.name, email: member.email, phone: member.phone_number, password: '', aadhar: member.aadhar_number || '' }
      : EMPTY);
    setMode(nextMode);
  };
  const close = () => setMode(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setError('');
    if (mode === 'password') {
      setSaving(true);
      try {
        await api(`/staff/change_password/${current.id}`, { method: 'PUT', body: { password: form.password } });
        close();
      } catch (e) {
        setError(e.message);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!/^\d{12}$/.test(form.aadhar)) return setError('Aadhaar number must be exactly 12 digits.');
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) return setError('Enter a 10-digit phone number.');
    if (mode === 'add' && !files.aadhar) return setError('Upload the Aadhaar card — it is needed for verification.');

    setSaving(true);
    try {
      const uploaded = {};
      for (const [key, file] of Object.entries(files)) {
        if (file) uploaded[key] = await uploadFile(file, setProgress, 'staff-docs');
      }
      const imageUrl = photo ? await uploadFile(photo, setProgress, 'staff-docs') : undefined;
      const body = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone_number: form.phone.trim(),
        aadhar_number: form.aadhar,
        docs_list: { ...(current ? current.docs_list : {}), ...uploaded },
        image_url: imageUrl,
        vendor_id: vendor.id,
      };
      if (mode === 'add') {
        await api('/staff/add_staff', { method: 'POST', body: { ...body, password: form.password } });
      } else {
        await api(`/staff/update_staff/${current.id}`, { method: 'PUT', body });
      }
      close();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (member) => {
    try {
      await api(`/staff/toggle_status/${member.id}`, { method: 'PUT' });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const q = query.toLowerCase();
  const rows = (staff || []).filter((s) =>
    s.name.toLowerCase().includes(q) || (s.phone_number || '').includes(query) || (s.email || '').toLowerCase().includes(q));
  const awaiting = (staff || []).filter((s) => s.verification !== 'Verified').length;

  const titles = { add: 'Add technician', edit: 'Edit technician', password: 'Reset password', view: current && current.name };

  return (
    <Page>
      <PageHeader
        title="Field staff"
        subtitle="Technicians who visit customers on your behalf. Fortune Home Care verifies each one before they can sign in."
        actions={<button className="btn btn-primary" onClick={() => open('add')}><FaPlus /> Add technician</button>}
      />
      <Alert>{loadError}</Alert>
      {awaiting > 0 && (
        <Alert tone="info">{awaiting} {awaiting === 1 ? 'technician is' : 'technicians are'} not verified yet and cannot be assigned to jobs.</Alert>
      )}
      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by name, phone or email" />
          {staff && <span className="count">{rows.length} of {staff.length}</span>}
        </div>
        {!staff ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr><th>Technician</th><th>Phone</th><th>Verification</th><th>Active</th><th className="right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((m) => (
                  <tr key={m.id}>
                    <td data-label="Technician">
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <div className="avatar">{initials(m.name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <button className="link-btn strong" onClick={() => open('view', m)}>{m.name}</button>
                          <span className="sub">{m.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Phone" className="nowrap">{m.phone_number}</td>
                    <td data-label="Verification">
                      <Badge tone={verificationTone(m.verification)}>{m.verification || 'Pending'}</Badge>
                      {m.remarks && m.verification !== 'Verified' && <span className="sub" title={m.remarks}>{m.remarks}</span>}
                    </td>
                    <td data-label="Active">
                      {m.verification === 'Verified'
                        ? <Switch on={m.is_active} onClick={() => toggle(m)} label="Active" />
                        : <span className="small muted">After verification</span>}
                    </td>
                    <td className="actions-cell">
                      <button className="icon-btn" title="Documents" onClick={() => open('view', m)}><FaFileAlt /></button>
                      <button className="icon-btn" title="Edit" onClick={() => open('edit', m)}><FaEdit /></button>
                      <button className="icon-btn" title="Reset password" onClick={() => open('password', m)}><FaKey /></button>
                    </td>
                  </tr>
                )) : <EmptyRow cols={5}>{query ? 'No technicians match your search.' : 'No technicians yet. Add your first one.'}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!mode}
        title={titles[mode]}
        subtitle={mode === 'password' && current ? `New app password for ${current.name}.`
          : mode === 'edit' ? 'Saving changes sends the technician back for verification.'
            : mode === 'view' && current ? current.phone_number : undefined}
        onClose={close}
        onSubmit={mode === 'view' ? undefined : submit}
        wide={mode === 'add' || mode === 'edit'}
        footer={mode === 'view' ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>Close</button>
            <button type="button" className="btn btn-primary" onClick={() => open('edit', current)}><FaEdit /> Edit</button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (progress > 0 && progress < 100 ? `Uploading ${progress}%` : 'Saving…') : 'Save'}
            </button>
          </>
        )}
      >
        {mode === 'view' && current ? (
          <div className="stack" style={{ gap: 16 }}>
            <div className="row">
              <Badge tone={verificationTone(current.verification)}>{current.verification || 'Pending'}</Badge>
              {current.remarks && <span className="small muted">“{current.remarks}”</span>}
            </div>
            <dl className="kv">
              <div><dt>Email</dt><dd>{current.email}</dd></div>
              <div><dt>Aadhaar</dt><dd className="mono">{current.aadhar_number}</dd></div>
            </dl>
            <div>
              <div className="section-label" style={{ marginBottom: 8 }}>Documents</div>
              {Object.entries(current.docs_list || {}).filter(([, u]) => u).length ? (
                <div className="grid grid-2" style={{ gap: 8 }}>
                  {Object.entries(current.docs_list).filter(([, u]) => u).map(([key, url]) => (
                    <a key={key} className="doc-tile" href={documentLink(url)} target="_blank" rel="noopener noreferrer">
                      <FaFileAlt /><span className="doc-name">{docLabel(key)}</span>
                    </a>
                  ))}
                </div>
              ) : <p className="muted">None uploaded.</p>}
            </div>
          </div>
        ) : (
          <div className="form">
            <Alert>{error}</Alert>
            {mode === 'password' ? (
              <Field label="New password" help="At least 6 characters. Share it with the technician.">
                <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} autoFocus />
              </Field>
            ) : (
              <>
                <div className="form-grid">
                  <Field label="Full name">
                    <input className="input" value={form.name} onChange={set('name')} required />
                  </Field>
                  <Field label="Phone (used to sign in)">
                    <input className="input" inputMode="tel" value={form.phone} onChange={set('phone')} required placeholder="10-digit mobile" />
                  </Field>
                  <Field label="Email">
                    <input className="input" type="email" value={form.email} onChange={set('email')} required />
                  </Field>
                  <Field label="Aadhaar number">
                    <input className="input" inputMode="numeric" maxLength={12} value={form.aadhar}
                      onChange={(e) => setForm({ ...form, aadhar: e.target.value.replace(/\D/g, '') })} required placeholder="12 digits" />
                  </Field>
                  {mode === 'add' && (
                    <Field label="App password" help="At least 6 characters.">
                      <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} />
                    </Field>
                  )}
                  <Field label="Photo" help={photo ? photo.name : 'Optional'}>
                    <label className="dropzone compact">
                      <FaCloudUploadAlt /><span><strong>{photo ? 'Change photo' : 'Choose photo'}</strong></span>
                      <input type="file" accept="image/*" hidden onChange={(e) => setPhoto((e.target.files || [])[0] || null)} />
                    </label>
                  </Field>
                </div>
                <div>
                  <div className="section-label" style={{ marginBottom: 10 }}>Documents</div>
                  <div className="grid grid-2" style={{ gap: 10 }}>
                    {DOCS.map((key) => {
                      const existing = current && current.docs_list && current.docs_list[key];
                      const chosen = files[key];
                      return (
                        <label key={key} className={`dropzone compact${chosen || existing ? ' done' : ''}`}>
                          {chosen || existing ? <FaCheckCircle /> : <FaCloudUploadAlt />}
                          <span>
                            <strong>{docLabel(key)}</strong>
                            <em>{chosen ? chosen.name : existing ? 'Uploaded · replace' : key === 'aadhar' ? 'Required' : 'Optional'}</em>
                          </span>
                          <input type="file" accept="image/*,application/pdf" hidden
                            onChange={(e) => setFiles({ ...files, [key]: (e.target.files || [])[0] || null })} />
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </Page>
  );
};

export default ManageStaff;
